import React, { useState } from 'react';
import { elements, inputs, actions, feedback } from '@ecosystem/newron-design-system';
import { getIdentifiers } from '../../data/helpers';
import { Cells, Table } from '../../design/table2';
import {
  BasicTrackingFn,
  ColumnType,
  DataCellValue,
  DataRow,
  DataType,
  FormatHint,
  IdentifierColumn,
  KpiWithChangeColumn,
  SortOption,
  SortingInfo,
} from '../../types';
import { getSortKeysFromColumn, GroupInfo, LeaderColumn } from './data';
import { CheckboxState } from '@ecosystem/newron-design-system/build/exports/inputs';
import { Formatting } from '../../data/formatting';
import { ProductGroupMenu } from './productGroupMenu';
import { getLeaderboardHeaderString, sortingColumnOptions } from './helper';
import { CELL_VALUE_MULTIPLE } from './constants';
import { LeaderboardType } from './types';
import { useMobile } from '../../context/mobile/mobile.provider';
import classNames from 'classnames';
import './board.parts.css';

const { DotsLoader } = feedback;

const { Icon } = elements;
const { IconButton } = actions;

type HeadersTopProps = {
  columns: LeaderColumn[];
  rowsCount: number;
  selectedCount: number;
  sortingInfo?: SortingInfo;
  sortingFn: (sorting: SortingInfo) => void;
  onSelectAll?: () => void;
  onClearSelect?: () => void;
  trackEvent: BasicTrackingFn;
};
function HeadersTop({
  columns,
  rowsCount,
  selectedCount,
  onClearSelect,
  onSelectAll,
  sortingFn,
  trackEvent,
  sortingInfo,
}: HeadersTopProps) {
  const { Checkbox, CheckboxState } = inputs;
  const [showOptions, setShowOptions] = useState(false);
  const { isMobile } = useMobile();

  const getCheckState = (): CheckboxState => {
    if (selectedCount > 0 && selectedCount < rowsCount) {
      return CheckboxState.INDETERMINATE;
    } else if (selectedCount > 0 && selectedCount === rowsCount) {
      return CheckboxState.CHECKED;
    }
    return CheckboxState.UNCHECKED;
  };

  const handleCheckState = () => {
    if (getCheckState() === CheckboxState.UNCHECKED) {
      onSelectAll();
    } else {
      onClearSelect();
    }
  };

  const columnsWithoutNulls = columns.filter(col => !!col.column);

  return (
    <>
      {onClearSelect !== undefined && onSelectAll !== undefined && (
        <Table.Column minWidth rowSpan={2} borders="b" align="center" isStickyLeft={isMobile}>
          <Checkbox state={getCheckState()} onChange={handleCheckState} testId="select-all" />
        </Table.Column>
      )}

      {columnsWithoutNulls.map((col, idx) => {
        const { hasSorting, identifier, isSorted } = sortingColumnOptions(col, 0, sortingInfo);
        const position = idx >= columnsWithoutNulls.length - 2 ? 'right' : 'left';

        return (
          <Table.Column
            key={col.column.name}
            align="left"
            rowSpan={col.subLabels ? 1 : 2}
            colSpan={col.subLabels?.length || 1}
            borders="rb"
            showHover={hasSorting}
            isSorted={isSorted}
            isStickyLeft={isMobile && idx == 0}
          >
            <div role="presentation" className="flex w-full" onClick={() => setShowOptions(!showOptions)}>
              {hasSorting ? (
                <Table.Sorting
                  colId={identifier}
                  colLabel={col.column.label}
                  value={sortingInfo}
                  columnIndex={idx}
                  options={col.column.sortOptions}
                  onSort={sortingFn}
                  position={position}
                  trackEvent={trackEvent}
                >
                  {col.column.label}
                </Table.Sorting>
              ) : (
                <span>{col.column.label}</span>
              )}
            </div>
          </Table.Column>
        );
      })}
    </>
  );
}

type HeadersBottomProps = {
  columns: LeaderColumn[];
  sortingInfo: SortingInfo;
  isLoading: boolean;
  sortingFn: (sorting: SortOption) => void;
  trackEvent: BasicTrackingFn;
};
function HeadersBottom({ columns, sortingInfo, isLoading, sortingFn, trackEvent }: HeadersBottomProps) {
  const colsToUse = columns.filter(col => col.subLabels);
  const { isMobile } = useMobile();

  return (
    <>
      {colsToUse.flatMap(col =>
        col.subLabels.map((label, idx) => {
          const { identifier, isSorted } = sortingColumnOptions(col, idx, sortingInfo);
          const columnPosition = columns.findIndex(
            cl => `${cl.column.name}.${col.column.label}` === `${col.column.name}.${col.column.label}`
          );
          const position = columnPosition >= columns.length - 2 ? 'right' : 'left';
          const isEnrichmentLoading = isLoading && !!col.column.enrichment;
          return (
            <Table.Column
              key={`${col.column.name}_${idx}`}
              align="center"
              showHover={true}
              borders={`b${idx === 0 ? 'l' : idx === col.subLabels.length - 1 ? 'r' : ''}`}
              isSorted={isSorted}
              isStickyLeft={isMobile && !col.subLabels}
            >
              <Table.Sorting
                colId={identifier}
                colLabel={col.column.label}
                options={col.column.sortOptions}
                onSort={sortingFn}
                value={sortingInfo}
                columnIndex={idx}
                position={position}
                trackEvent={trackEvent}
              >
                {isEnrichmentLoading ? <DotsLoader stretch height={'24px'} /> : <HeaderLabel label={label} />}
              </Table.Sorting>
            </Table.Column>
          );
        })
      )}
    </>
  );
}

type LabelProps = {
  label: string | string[];
};
function HeaderLabel({ label }: LabelProps) {
  if (Array.isArray(label)) {
    return (
      <>
        {label.map(l => (
          <span key={l} className="block nowrap">
            {l}
          </span>
        ))}
      </>
    );
  }

  return <span className="block nowrap">{label}</span>;
}

type InfoRowProps = {
  column: IdentifierColumn;
  row: DataRow;
  onSelect?: (rowId: string) => void;
  onHighlight?: (rowId: string) => void;
  isSelected?: boolean;
  isHighlighted?: boolean;
  isDisabled?: boolean;
  group?: GroupInfo;
  allGroups?: GroupInfo[];
  onDeleteGroup?: (name: string) => void;
  onDeleteGroupItem?: (name: string, itemId: string) => void;
  onEditGroup?: (oldName: string, newName: string) => void;
  isGroupVisible?: boolean;
  isSorted?: boolean;
  rowIdField: string;
  leaderboardType: LeaderboardType;
  isLoading?: boolean;
};
function InfoRowCells({
  column,
  row,
  onSelect,
  isSelected,
  isHighlighted,
  onHighlight,
  isDisabled,
  isLoading,
  isSorted,
  group,
  onDeleteGroup,
  onDeleteGroupItem,
  onEditGroup,
  isGroupVisible,
  allGroups,
  rowIdField,
  leaderboardType,
}: InfoRowProps) {
  const { id, name } = getIdentifiers(column, row, true);

  const { isMobile } = useMobile();
  const groupName = group?.name;

  const isGroup = !!groupName;

  const handleSelect = () => {
    onSelect(id);
  };

  const handleHighlight = () => {
    onHighlight(id);
  };

  const { Checkbox, CheckboxState } = inputs;

  const handleEditGroupName = newName => {
    onEditGroup(groupName, newName);
  };

  const handleRemoveItem = (groupName: string, itemId: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteGroupItem(groupName, itemId);
  };

  const getFilteredProductNumber = (difference: number) => {
    return difference > 0 ? ` (${group.groupModelIds.length - group.data.length} filtered)` : '';
  };

  const getCountGroupsForMobile = (difference: number) => {
    return difference > 0 ? `(${group.data.length})` : `(${group.groupModelIds.length})`;
  };

  const getCountTextForGroups = () => {
    const difference = group.groupModelIds.length - group.data.length;
    return isMobile
      ? getCountGroupsForMobile(difference)
      : `${group.groupModelIds.length} ${getLeaderboardHeaderString(
          leaderboardType,
          group.groupModelIds.length
        )} ${getFilteredProductNumber(difference)}`;
  };

  return (
    <>
      {onSelect !== undefined ? (
        <Table.Cell align="center" selected={isSelected} isStickyLeft={isMobile}>
          <Checkbox
            onChange={handleSelect}
            state={isSelected ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
            disabled={isDisabled}
          />
        </Table.Cell>
      ) : (
        <Table.Cell align="center" isStickyLeft={isMobile}>
          <div />
        </Table.Cell>
      )}
      <Table.Cell
        align="left"
        onClick={onHighlight ? handleHighlight : undefined}
        selected={isHighlighted || false}
        isSorted={isSorted}
        isStickyLeft={isMobile}
      >
        <div
          className={classNames('flex items-center justify-between gap-4', {
            'w-24': isMobile,
          })}
        >
          {isGroup && (
            <Icon size="sm" color="brand">
              {isGroupVisible ? <Icon.icons.FolderOpen /> : <Icon.icons.Folder />}
            </Icon>
          )}
          <div
            className={classNames({
              'flex-row w-24 min-w-0': isMobile,
              'flex-column w-48': !isMobile,
            })}
          >
            <span
              className={`block text-brand ${isMobile ? 'mobile-table-header-column-truncate' : 'overflow-ellipses'}`}
            >
              {name}
            </span>
            {isGroup && (
              <span
                className={classNames('block text-inactive font-normal', {
                  'text-xs': isMobile,
                  'self-end': isMobile,
                })}
              >
                {getCountTextForGroups()}
              </span>
            )}
          </div>
          {row.values.group && !isMobile && (
            <IconButton
              testId="remove-group-item"
              disabled={isLoading}
              onClick={handleRemoveItem(row.values.group.toString(), row.values[`${rowIdField}`].toString())}
            >
              <IconButton.icons.RemoveOutline />
            </IconButton>
          )}
          {isGroup && (
            <ProductGroupMenu
              disabled={isLoading}
              allGroups={allGroups?.map(group => group.name)}
              group={groupName}
              onDelete={onDeleteGroup}
              onEdit={handleEditGroupName}
            />
          )}
        </div>
      </Table.Cell>
    </>
  );
}

type DataRowProps = {
  rowId: string;
  columns: LeaderColumn[];
  row: DataRow;
  onHighlight?: (rowId: string) => void;
  isHighlighted?: boolean;
  sortInfo?: SortingInfo;
};
function DataRowCells({ rowId, columns, row, onHighlight, isHighlighted, sortInfo }: DataRowProps) {
  const dataColumns = columns.slice(1);
  const handleClick = () => {
    onHighlight?.(rowId);
  };

  return (
    <>
      {dataColumns.map(col => (
        <DataCell
          key={`${rowId}_${col.column.name}`}
          isSelected={isHighlighted || false}
          column={col}
          row={row}
          rowId={rowId}
          onClick={onHighlight ? handleClick : undefined}
          sortInfo={sortInfo}
        />
      ))}
    </>
  );
}

type DataCellProps = {
  column: LeaderColumn;
  row: DataRow;
  rowId: string;
  isSelected: boolean;
  onClick?: () => void;
  sortInfo?: SortingInfo;
};
function DataCell(props: DataCellProps) {
  const { type } = props.column.column;

  switch (type) {
    case ColumnType.Kpi:
    case ColumnType.KpiWithChange:
      return <KpiCell {...props} />;
    case ColumnType.Date:
      return <DateCell {...props} />;
    case ColumnType.Number:
      return <NumberCell {...props} />;
    default:
      return <TextCell {...props} />;
  }
}

function KpiCell({ column, row, rowId, isSelected, onClick, sortInfo }: DataCellProps) {
  const {
    column: { name, arrayLength, dataType, formatHint, type: columnType },
    currency,
  } = column;
  const valuesIndex = Array(arrayLength || 1).fill(null);

  let changeValues: DataCellValue | undefined;
  let changeFormatHint: FormatHint | undefined;
  let changeDataType: DataType | undefined;
  if (columnType === ColumnType.KpiWithChange) {
    const changeColumn = column.column as KpiWithChangeColumn;
    changeValues = row.values[changeColumn.changeName];
    changeFormatHint = changeColumn.changeFormatHint;
    changeDataType = changeColumn.changeDataType;
  }

  return (
    <>
      {valuesIndex.map((_, idx) => (
        <Table.Cell
          key={`${rowId}_${name}_${idx}`}
          borders={`b${idx === 0 ? 'l' : idx === valuesIndex.length - 1 ? 'r' : ''}`}
          selected={isSelected}
          onClick={onClick}
          important={rowId === 'market'}
          isSorted={getSortKeysFromColumn(column).includes(sortInfo?.sortKey) && sortInfo?.sortIndex === idx}
        >
          {Formatting.format(row.values[name]?.[idx] ?? null, {
            dataType,
            formatHint: formatHint,
            hyphenForNull: true,
            currency,
          })}
          {changeDataType && (
            <Cells.ValueChange
              value={Formatting.format(changeValues?.[idx] ?? null, {
                dataType: changeDataType,
                formatHint: changeFormatHint,
                currency,
              })}
              raw={changeValues?.[idx]}
            />
          )}
        </Table.Cell>
      ))}
    </>
  );
}

function DateCell({ column, row, isSelected, onClick, sortInfo }: DataCellProps) {
  const {
    column: { name, formatHint, dataType },
    currency,
  } = column;
  const value = String(row.values[name] || '-');
  const date = value !== '-' && value !== CELL_VALUE_MULTIPLE ? new Date(value) : undefined;
  const formatted = date ? Formatting.format(date, { formatHint, dataType, currency }) : getFormattedCellValue(value);
  return (
    <Table.Cell
      align="left"
      borders="bl"
      selected={isSelected}
      onClick={onClick}
      isSorted={getSortKeysFromColumn(column).includes(sortInfo?.sortKey)}
    >
      {formatted}
    </Table.Cell>
  );
}

function TextCell({ column, row, isSelected, onClick, sortInfo }: DataCellProps) {
  let text = String(row.values[column.column.name] || '-');
  if (column.column.type === ColumnType.Identifier) {
    const { name, description } = getIdentifiers(column.column as IdentifierColumn, row);
    text = name || description;
  }
  return (
    <Table.Cell
      align="left"
      borders="bl"
      selected={isSelected}
      onClick={onClick}
      isSorted={getSortKeysFromColumn(column).includes(sortInfo?.sortKey)}
    >
      {getFormattedCellValue(text)}
    </Table.Cell>
  );
}

function NumberCell({ column, row, isSelected, onClick, sortInfo }: DataCellProps) {
  const { column: dataColumn, currency } = column;
  const value = row.values[dataColumn.name] ?? null;
  const formatted = !isNaN(Number(value))
    ? Formatting.format(Number(value), {
        dataType: dataColumn.dataType,
        formatHint: dataColumn.formatHint,
        hyphenForNull: true,
        currency,
      })
    : getFormattedCellValue(String(value));
  return (
    <Table.Cell
      align="right"
      borders="bl"
      selected={isSelected}
      onClick={onClick}
      isSorted={getSortKeysFromColumn(column).includes(sortInfo?.sortKey)}
    >
      {formatted}
    </Table.Cell>
  );
}

function getFormattedCellValue(value: string) {
  return value === CELL_VALUE_MULTIPLE ? <span className="text-inactive font-normal"> {value} </span> : value;
}

export { HeadersTop, HeadersBottom, InfoRowCells, DataRowCells };
